import { useCallback, useEffect, useState } from 'react';
import { useLatest } from 'react-use';

export interface AsyncLayout<TResult> {
  /**
   * The result of the last layout that completed. It stays available while a
   * new layout runs and after a layout fails, so the canvas does not go empty.
   */
  result: TResult | null;
  isPending: boolean;
  didFail: boolean;
  /** Runs the layout again with the current input. */
  retry: () => void;
}

/**
 * Runs an asynchronous diagram layout, such as ELK, each time `input` changes.
 * A null input runs no layout. The hook compares inputs with `Object.is`, so
 * pass a primitive value or a memoized object.
 *
 * Only the layout for the current input can set the result. The hook discards
 * the result or the error of a layout whose input changed, or whose component
 * unmounted, before the layout completed. A failure does not start a new
 * attempt; call `retry` for that.
 *
 * The hook calls the latest `solve` and `onError` functions, so they do not
 * need to be memoized.
 */
export function useAsyncLayout<TInput, TResult>(
  input: TInput | null,
  solve: (input: TInput) => Promise<TResult>,
  onError?: (error: unknown, input: TInput) => void
): AsyncLayout<TResult> {
  const [result, setResult] = useState<TResult | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [didFail, setDidFail] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const callbacks = useLatest({ solve, onError });
  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  useEffect(() => {
    setDidFail(false);
    if (input === null) {
      setIsPending(false);
      return;
    }

    let wasCancelled = false;
    setIsPending(true);
    callbacks.current
      .solve(input)
      .then((solved) => {
        if (wasCancelled) return;
        setResult(() => solved);
        setIsPending(false);
      })
      .catch((error: unknown) => {
        if (wasCancelled) return;
        callbacks.current.onError?.(error, input);
        setIsPending(false);
        setDidFail(true);
      });

    return () => {
      wasCancelled = true;
    };
  }, [input, attempt, callbacks]);

  return { result, isPending, didFail, retry };
}
